@if (isOpen()) {
  <div data-testid="chat-backdrop" class="chat-backdrop fixed inset-0 z-[150] bg-black/40" (click)="close()" aria-hidden="true"></div>

  <aside class="chat-drawer fixed inset-y-0 right-0 z-[151] w-full sm:max-w-md bg-white shadow-2xl flex flex-col"
    role="dialog" aria-modal="true" [attr.aria-labelledby]="titleId">

    <!-- En-tête -->
    <header class="flex items-center gap-3 px-4 h-[72px] shrink-0 border-b border-gray-100">
      @if (context(); as ctx) {
        <div class="size-11 shrink-0 rounded-full overflow-hidden bg-main-50 text-main-700 flex items-center justify-center font-semibold text-lg">
          @if (ctx.contactPhoto) {
            <img [src]="ctx.contactPhoto" alt="" class="size-full object-cover">
          } @else {
            <span data-testid="chat-avatar-initial" aria-hidden="true">{{ initial() }}</span>
          }
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <h2 [id]="titleId" class="text-base font-semibold text-main-900 truncate">{{ ctx.contactName }}</h2>
            <span class="shrink-0 rounded-full bg-primary-light border border-main-100 px-2 py-0.5 text-[11px] font-medium text-main-700">
              {{ ('CHAT.STATE.' + ctx.state) | translate }}
            </span>
          </div>
          @if (reservationLine()) {
            <p class="text-xs text-gray-500 truncate">{{ reservationLine() }}</p>
          }
        </div>
      } @else {
        <h2 [id]="titleId" class="flex-1 text-base font-semibold text-main-900">{{ 'CHAT.TITLE' | translate }}</h2>
      }
      <button type="button" data-testid="chat-close" (click)="close()"
        class="size-10 shrink-0 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-main-500 cursor-pointer"
        [attr.aria-label]="'CHAT.CLOSE' | translate">
        <icon-close [size]="20" aria-hidden="true" />
      </button>
    </header>

    <!-- Fil de messages -->
    <div #scroller (scroll)="onScroll()" data-lenis-prevent
      class="flex-1 overflow-y-auto overscroll-contain bg-gray-50 px-4 py-4">
      @if (loading()) {
        <div class="h-full flex flex-col items-center justify-center gap-3 text-gray-500" role="status">
          <lucide-icon [img]="icons.LoaderCircle" [size]="28" class="animate-spin text-main-500" aria-hidden="true" />
          <span class="text-sm">{{ 'CHAT.LOADING' | translate }}</span>
        </div>
      } @else if (error() === 'forbidden') {
        <div class="h-full flex items-center justify-center text-center px-6" role="alert">
          <p class="text-sm text-gray-600">{{ 'CHAT.ERROR_FORBIDDEN' | translate }}</p>
        </div>
      } @else if (error()) {
        <div class="h-full flex flex-col items-center justify-center text-center gap-4 px-6" role="alert">
          <p class="text-sm text-gray-600">{{ 'CHAT.ERROR_GENERIC' | translate }}</p>
          <button type="button" data-testid="chat-retry" (click)="retry()"
            class="rounded-full bg-main-500 hover:bg-main-600 text-white px-6 py-2.5 text-sm font-medium transition-colors cursor-pointer">
            {{ 'CHAT.RETRY' | translate }}
          </button>
        </div>
      } @else {
        @if (hasMore()) {
          <div class="flex justify-center mb-4">
            <button type="button" data-testid="chat-load-older" (click)="loadOlder()" [disabled]="loadingOlder()"
              class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 transition-colors cursor-pointer">
              @if (loadingOlder()) {
                <lucide-icon [img]="icons.LoaderCircle" [size]="14" class="animate-spin" aria-hidden="true" />
              }
              {{ 'CHAT.LOAD_OLDER' | translate }}
            </button>
          </div>
        }

        @if (messages().length === 0) {
          <div class="h-full flex items-center justify-center text-center px-6">
            <p class="text-sm text-gray-500">{{ 'CHAT.EMPTY' | translate }}</p>
          </div>
        } @else {
          <div role="log" aria-live="polite" class="flex flex-col gap-1">
            @for (group of groups(); track group.day) {
              <div class="flex justify-center my-3">
                <span data-testid="chat-day" class="rounded-full bg-white border border-gray-200 px-3 py-1 text-[11px] font-medium text-gray-500 first-letter:uppercase">
                  @if (group.labelKey) {
                    {{ group.labelKey | translate }}
                  } @else {
                    {{ group.label }}
                  }
                </span>
              </div>

              @for (m of group.messages; track m.id) {
                @if (m.type === 'system') {
                  <div data-testid="chat-system" class="flex justify-center my-1">
                    <p class="max-w-[85%] text-center text-xs italic text-gray-500 whitespace-pre-wrap break-words">{{ m.content }}</p>
                  </div>
                } @else {
                  @let mine = isMine(m);
                  <div data-testid="chat-message" class="flex" [class.justify-end]="mine" [class.justify-start]="!mine">
                    <div class="max-w-[80%] rounded-2xl px-3.5 py-2 shadow-sm"
                      [class]="mine ? 'bg-main-600 text-white rounded-br-md' : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md'">
                      @if (m.type === 'image') {
                        <a [href]="m.content" target="_blank" rel="noopener noreferrer" class="block -mx-1.5 -mt-0.5 mb-1">
                          <img [src]="m.content" [alt]="'CHAT.IMAGE_ALT' | translate" loading="lazy" class="max-h-64 w-auto rounded-xl object-cover">
                        </a>
                      } @else {
                        <p class="text-sm leading-5 whitespace-pre-wrap break-words">{{ m.content }}</p>
                      }
                      <div class="mt-0.5 flex items-center justify-end gap-1 text-[11px] leading-none"
                        [class]="mine ? 'text-white/80' : 'text-gray-500'">
                        <span>{{ time(m) }}</span>
                        @if (mine) {
                          <span data-testid="chat-tick" class="inline-flex">
                            @if (m.status === 'read') {
                              <lucide-icon [img]="icons.CheckCheck" [size]="14" aria-hidden="true" />
                              <span class="sr-only">{{ 'CHAT.READ' | translate }}</span>
                            } @else {
                              <lucide-icon [img]="icons.Check" [size]="14" aria-hidden="true" />
                              <span class="sr-only">{{ 'CHAT.SENT' | translate }}</span>
                            }
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                }
              }
            }
          </div>
        }
      }
    </div>

    <!-- Saisie -->
    <div class="shrink-0 border-t border-gray-100 bg-white px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div class="min-h-5 px-1 pb-1" aria-live="polite">
        @if (isContactTyping() && context(); as ctx) {
          <p class="flex items-center gap-1.5 text-xs text-gray-500">
            <span class="inline-flex gap-0.5" aria-hidden="true">
              <span class="chat-dot size-1 rounded-full bg-gray-400"></span>
              <span class="chat-dot size-1 rounded-full bg-gray-400"></span>
              <span class="chat-dot size-1 rounded-full bg-gray-400"></span>
            </span>
            {{ 'CHAT.TYPING' | translate: { name: ctx.contactName } }}
          </p>
        }
      </div>
      @if (sendError()) {
        <p class="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">{{ 'CHAT.ERROR_SEND' | translate }}</p>
      }
      <form class="flex items-end gap-2" (submit)="$event.preventDefault(); submit()">
        <textarea #composer rows="1" maxlength="2000"
          [value]="draft()" (input)="onInput($event)" (keydown)="onKeydown($event)"
          [disabled]="!!error()"
          [attr.placeholder]="'CHAT.PLACEHOLDER' | translate"
          [attr.aria-label]="'CHAT.PLACEHOLDER' | translate"
          class="flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm leading-5 max-h-[120px] overflow-y-auto focus:outline-none focus:border-main-500 focus:bg-white disabled:opacity-60 transition-colors"></textarea>
        <button type="submit" data-testid="chat-send" [disabled]="!canSend()"
          [attr.aria-label]="'CHAT.SEND' | translate"
          class="size-11 shrink-0 rounded-full bg-main-500 hover:bg-main-600 text-white flex items-center justify-center transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-main-500 cursor-pointer">
          @if (sending()) {
            <lucide-icon [img]="icons.LoaderCircle" [size]="20" class="animate-spin" aria-hidden="true" />
          } @else {
            <lucide-icon [img]="icons.SendHorizontal" [size]="20" aria-hidden="true" />
          }
        </button>
      </form>
    </div>
  </aside>
}
